import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "../lib/api";
import Topbar from "../components/Topbar";
import KpiCard from "../components/KpiCard";
import StatusBadge from "../components/StatusBadge";
import EmptyState from "../components/EmptyState";
import InvoiceUploadModal from "../components/InvoiceUploadModal";
import POQuantityChangeModal from "../components/POQuantityChangeModal";
import { useAuth } from "../lib/auth";
import { ShoppingCart, FileCheck, Wallet, FileUp, MailWarning, Boxes, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export default function SupplierDashboard() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [uploadFor, setUploadFor] = useState(null);
  const [uploadNoPo, setUploadNoPo] = useState(false);
  const [qtyChangeFor, setQtyChangeFor] = useState(null);
  const [pendingQty, setPendingQty] = useState({});
  const [emailVerified, setEmailVerified] = useState(user?.email_verified !== false);
  const [resending, setResending] = useState(false);

  const load = async () => {
    const [p, i] = await Promise.all([api.get("/purchase-orders"), api.get("/invoices")]);
    setPos(p.data);
    setInvoices(i.data);
    try {
      const q = await api.get("/quantity-changes?status=pending");
      setPendingQty(Object.fromEntries((q.data || []).map((r) => [r.purchase_order_id, r])));
    } catch {}
    try {
      const me = await api.get("/auth/me");
      setEmailVerified(me.data.email_verified !== false);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/auth/send-email-verification");
      toast.success(t("verifyEmail.resent"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setResending(false);
    }
  };

  const newPos = pos.filter((p) => p.status === "approved" || p.status === "received");
  const pendingPay = invoices.filter((i) => i.status !== "paid").length;
  const totalMonth = invoices
    .filter((i) => new Date(i.issued_at).getMonth() === new Date().getMonth())
    .reduce((a, b) => a + b.total, 0);

  return (
    <>
      <Topbar title={t("dashboard.supplierPanel")} subtitle={t("dashboard.supplierPanelSubtitle", { name: user?.full_name })} />
      <div className="p-6 space-y-6">
        {!emailVerified && (
          <div data-testid="supplier-email-unverified-banner" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
            <MailWarning className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-amber-900">{t("verifyEmail.bannerTitle")}</div>
              <p className="text-sm text-amber-800 mt-0.5">{t("verifyEmail.bannerText")}</p>
            </div>
            <button
              data-testid="supplier-resend-verification-btn"
              onClick={resendVerification}
              disabled={resending}
              className="text-sm font-bold text-amber-900 underline decoration-amber-400 underline-offset-2 hover:decoration-2 whitespace-nowrap disabled:opacity-50"
            >
              {resending ? t("common.saving") : t("verifyEmail.resendCta")}
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard testId="kpi-sup-new" label={t("dashboard.kpis.supNew")} value={newPos.length} icon={ShoppingCart} accent={newPos.length > 0} />
          <KpiCard testId="kpi-sup-pending-pay" label={t("dashboard.kpis.supPendingPay")} value={pendingPay} icon={FileCheck} />
          <KpiCard testId="kpi-sup-month" label={t("dashboard.kpis.supMonth")} value={fmtMXN(totalMonth)} icon={Wallet} />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-6 pb-3 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-display font-bold text-lg">{t("dashboard.receivedPos")}</h2>
              <p className="text-xs text-slate-500">{t("dashboard.uploadInvoiceWhen")}</p>
            </div>
            <button
              onClick={() => setUploadNoPo(true)}
              data-testid="upload-invoice-no-po-btn"
              className="btn-primary-chizy px-3.5 py-2 rounded-xl text-xs flex items-center gap-1.5"
            >
              <FileUp className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("invoices.uploadNoPo")}
            </button>
          </div>
          {pos.length === 0 ? (
            <div className="p-6"><EmptyState title={t("dashboard.noOrdersYetTitle")} message={t("dashboard.noOrdersYetMsg")} /></div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.folio")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("po.branch")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                  <th className="text-right px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.total")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.status")}</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {pos.map((po, idx) => (
                  <motion.tr key={po.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-6 py-3.5 font-mono text-sm font-semibold">{po.folio}</td>
                    <td className="px-6 py-3.5 text-sm text-slate-700">{po.branch_name}</td>
                    <td className="px-6 py-3.5 text-sm text-slate-500">{fmtDate(po.created_at)}</td>
                    <td className="px-6 py-3.5 text-sm font-semibold text-right">{fmtMXN(po.total)}</td>
                    <td className="px-6 py-3.5"><StatusBadge status={po.status} /></td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        {pendingQty[po.id] ? (
                          <span data-testid={`po-qty-pending-badge-${po.folio}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-[11px] font-bold">
                            <Clock className="w-3 h-3" /> {t("poQty.pendingBadge")}
                          </span>
                        ) : po.status === "approved" ? (
                          <button
                            onClick={() => setQtyChangeFor(po)}
                            data-testid={`po-qty-propose-btn-${po.folio}`}
                            className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg text-xs font-bold flex items-center gap-1.5"
                          >
                            <Boxes className="w-3 h-3" strokeWidth={2.5} /> {t("poQty.propose")}
                          </button>
                        ) : null}
                        {(po.status === "approved" || po.status === "received") && !pendingQty[po.id] && (
                          <button
                            onClick={() => setUploadFor(po)}
                            data-testid={`upload-invoice-btn-${po.folio}`}
                            className="btn-primary-chizy px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5"
                          >
                            <FileUp className="w-3 h-3" strokeWidth={2.5} /> {t("dashboard.uploadInvoice")}
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {uploadFor && (
        <InvoiceUploadModal
          po={uploadFor}
          onClose={() => setUploadFor(null)}
          onSuccess={load}
        />
      )}

      {qtyChangeFor && (
        <POQuantityChangeModal
          poId={qtyChangeFor.id}
          onClose={() => setQtyChangeFor(null)}
          onSuccess={load}
        />
      )}

      {uploadNoPo && (
        <InvoiceUploadModal
          po={null}
          onClose={() => setUploadNoPo(false)}
          onSuccess={load}
        />
      )}
    </>
  );
}
